/**
 * Chart.js builders for the DIFOT dashboard. Chart.js is bundled (not loaded
 * from a CDN) so it complies with the Worker's strict `script-src 'self'` CSP.
 */
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartConfiguration,
} from 'chart.js';
import { formatPeriodLabel, periodKey, type Period, type Row } from './lib';

Chart.register(
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
);

export function buildTrendChart(ctx: CanvasRenderingContext2D, rows: Row[], period: Period): Chart {
  const buckets: Record<string, { total: number; complete: number; difot: number; failed: number; denom: number }> = {};
  rows.forEach((r) => {
    const k = periodKey(r.d, period);
    if (!buckets[k]) buckets[k] = { total: 0, complete: 0, difot: 0, failed: 0, denom: 0 };
    buckets[k].total++;
    if (r.st !== 'Cancelled') buckets[k].denom++;
    if (r.st === 'Complete') buckets[k].complete++;
    if (r.st === 'Complete' && r.ot === true) buckets[k].difot++;
    if (r.st === 'Failed') buckets[k].failed++;
  });
  const keys = Object.keys(buckets).sort();
  const labels = keys.map((k) => formatPeriodLabel(k, period));
  const difotData = keys.map((k) => (buckets[k].denom ? +((buckets[k].difot / buckets[k].denom) * 100).toFixed(1) : null));
  const volData = keys.map((k) => buckets[k].total);
  const failData = keys.map((k) => buckets[k].failed);

  const config: ChartConfiguration = {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Stops',
          data: volData,
          backgroundColor: 'rgba(0,97,162,0.22)',
          borderColor: 'rgba(0,97,162,0.5)',
          borderWidth: 1,
          yAxisID: 'y2',
          borderRadius: 6,
          maxBarThickness: 80,
        },
        {
          type: 'bar',
          label: 'Failed',
          data: failData,
          backgroundColor: 'rgba(255,82,69,0.7)',
          yAxisID: 'y2',
          stack: 'a',
          borderRadius: 6,
          maxBarThickness: 80,
        },
        {
          type: 'line',
          label: 'DIFOT %',
          data: difotData,
          borderColor: '#0BDA8E',
          backgroundColor: 'rgba(11,218,142,0.12)',
          borderWidth: 3,
          pointRadius: 5,
          pointBackgroundColor: '#0BDA8E',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          fill: true,
          tension: 0.35,
          yAxisID: 'y',
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: '#003D6C', titleColor: '#fff', bodyColor: '#fff', padding: 10, cornerRadius: 6 },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: '#737474', font: { size: 11 } } },
        y: {
          position: 'left',
          min: 0,
          max: 100,
          ticks: { callback: (v) => v + '%', color: '#737474', font: { size: 11 } },
          grid: { color: '#F1F3F7' },
          title: { display: true, text: 'DIFOT %', color: '#737474', font: { size: 11 } },
        },
        y2: {
          position: 'right',
          beginAtZero: true,
          ticks: { color: '#737474', font: { size: 11 } },
          grid: { display: false },
          title: { display: true, text: 'Stops', color: '#737474', font: { size: 11 } },
        },
      },
    },
  };
  return new Chart(ctx, config);
}

export function buildZoneChart(ctx: CanvasRenderingContext2D, rows: Row[]): Chart {
  const byZone: Record<string, { total: number; complete: number; failed: number; pending: number }> = {};
  rows.forEach((r) => {
    const z = r.r || 'Other';
    if (!byZone[z]) byZone[z] = { total: 0, complete: 0, failed: 0, pending: 0 };
    byZone[z].total++;
    if (r.st === 'Complete') byZone[z].complete++;
    if (r.st === 'Failed') byZone[z].failed++;
    if (r.st === 'Pending' || r.st === 'Enroute' || r.st === 'Arrived') byZone[z].pending++;
  });
  const order = Object.keys(byZone).sort((a, b) => byZone[b].total - byZone[a].total);

  const config: ChartConfiguration = {
    type: 'bar',
    data: {
      labels: order,
      datasets: [
        {
          label: 'Delivered',
          data: order.map((z) => byZone[z].complete),
          backgroundColor: '#0BDA8E',
          stack: 'a',
          borderRadius: { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 },
        },
        { label: 'Failed', data: order.map((z) => byZone[z].failed), backgroundColor: '#FF5245', stack: 'a' },
        {
          label: 'Pending',
          data: order.map((z) => byZone[z].pending),
          backgroundColor: '#B37D6C',
          stack: 'a',
          borderRadius: { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 },
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: {
          position: 'bottom',
          labels: { usePointStyle: true, pointStyle: 'rectRounded', font: { size: 11 }, color: '#737474', boxWidth: 8, padding: 14 },
        },
        tooltip: { backgroundColor: '#003D6C', padding: 10, cornerRadius: 6 },
      },
      scales: {
        x: { stacked: true, ticks: { color: '#737474', font: { size: 11 } }, grid: { color: '#F1F3F7' } },
        y: { stacked: true, ticks: { color: '#384553', font: { size: 12, weight: 600 } }, grid: { display: false } },
      },
    },
  };
  return new Chart(ctx, config);
}
